import { z } from 'zod';
import {
    CANCELLATION_REASONS,
    REQUESTING_PARTIES,
    SERVICE_HISTORY_SOURCES,
    type CancellationReason,
    type CancellationRequestingParty,
    type ServiceHistorySource,
} from '../types/cancellation.types';

export const CANCEL_NOTE_MAX_LENGTH = 1000;
export const CASE_REFERENCE_MAX_LENGTH = 100;
export const SERVICE_HISTORY_DETAIL_MAX_LENGTH = 200;

const isOneOf = <T extends string>(options: readonly T[], value: string): value is T =>
    (options as readonly string[]).includes(value);

const fields = z.object({
    reason: z.string(),
    note: z.string().trim().max(CANCEL_NOTE_MAX_LENGTH, `The note cannot exceed ${CANCEL_NOTE_MAX_LENGTH} characters`),
    requestingParty: z.string(),
    caseReference: z
        .string()
        .trim()
        .max(CASE_REFERENCE_MAX_LENGTH, `The case reference cannot exceed ${CASE_REFERENCE_MAX_LENGTH} characters`),
    serviceHistoryConfirmed: z.boolean(),
    serviceHistorySource: z.string(),
    serviceHistoryDetail: z
        .string()
        .trim()
        .max(SERVICE_HISTORY_DETAIL_MAX_LENGTH, `The detail cannot exceed ${SERVICE_HISTORY_DETAIL_MAX_LENGTH} characters`),
});

export type CancelAgreementFormInput = z.input<typeof fields>;

export interface CancelAgreementValues {
    reason: CancellationReason;
    note: string;
    requestingParty: CancellationRequestingParty;
    caseReference: string;
    serviceHistory: { source: ServiceHistorySource; detail: string } | null;
}

export function buildCancelAgreementSchema(requiresServiceHistory: boolean) {
    return fields
        .superRefine((values, ctx) => {
            const fail = (path: keyof CancelAgreementFormInput, message: string) =>
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

            if (!isOneOf(CANCELLATION_REASONS, values.reason)) fail('reason', 'Select a reason for the cancellation');
            if (!isOneOf(REQUESTING_PARTIES, values.requestingParty)) fail('requestingParty', 'Select who asked to cancel');
            if (values.reason === 'other' && values.note === '') fail('note', 'Add a note when the reason is Other');
            if (!requiresServiceHistory) return;
            if (!values.serviceHistoryConfirmed) fail('serviceHistoryConfirmed', 'Confirm that the service history has been checked');
            if (!isOneOf(SERVICE_HISTORY_SOURCES, values.serviceHistorySource)) {
                fail('serviceHistorySource', 'Select how the service history was confirmed');
            } else if (values.serviceHistorySource !== 'thirty_day_rule' && values.serviceHistoryDetail === '') {
                fail('serviceHistoryDetail', 'Enter the detail for this source');
            }
        })
        .transform(
            (values): CancelAgreementValues => ({
                reason: values.reason as CancellationReason,
                note: values.note,
                requestingParty: values.requestingParty as CancellationRequestingParty,
                caseReference: values.caseReference,
                serviceHistory: requiresServiceHistory
                    ? { source: values.serviceHistorySource as ServiceHistorySource, detail: values.serviceHistoryDetail }
                    : null,
            }),
        );
}
